import type { CustomCellEditorProps } from 'ag-grid-react';
import { get } from 'lodash-es';
import {
  AG_GRID_CURRENCY_CELL_RENDERER_KEY,
  type AgGridCurrencyParams,
  type AgGridSearchSelectDropdownProps,
} from '../../AgGrid';
import { wrapDisplayedCurrencyDescriptionQuickFilter } from './assetQuickFilterText';
import { baseColumn } from './baseColumn';
import type { ColDefFactory, Column } from './types';

/**
 * Column to render Currency (token, fiat, etc.) values from Field or securityCurrencyField param (if available)
 *
 * See {@link AgGridCurrencyParams} for parameter options.
 */
export const currency: ColDefFactory<Column<AgGridCurrencyParams>> = column => ({
  ...baseColumn(column),
  getQuickFilterText: wrapDisplayedCurrencyDescriptionQuickFilter(column.getQuickFilterText, column.params ?? {}),
  cellRenderer: AG_GRID_CURRENCY_CELL_RENDERER_KEY,
  cellEditor: 'searchSelectDropdown',
  cellEditorPopup: true,
  cellRendererParams: column.params,
  suppressKeyboardEvent: params => params.event.key === 'Enter',
  cellEditorParams: (params: CustomCellEditorProps) => {
    const currencies = column?.params?.currencies ?? params.context.current.currenciesBySymbol ?? new Map();
    return {
      ...params,
      useSearchSelectParams: {
        items: [...currencies.values()].map(currency => currency.Symbol),
        getLabel: item => item,
      },
    } satisfies AgGridSearchSelectDropdownProps<string>;
  },
  valueGetter: ({ data, context }) => {
    const { symbolField, securityCurrencyField } = column.params ?? {};
    if (symbolField && securityCurrencyField) {
      const symbol = get(data, symbolField);
      const security = context.current.securitiesBySymbol?.get(symbol);
      const currency = get(security, securityCurrencyField);
      if (currency) {
        return currency;
      }
    }
    if (!column.field || !data) {
      return undefined;
    }

    return get(data, column.field);
  },
});
